import { Link } from "../common/components/Link";
import { MobileForkPageNavigation } from "./MobileForkPageNavigation";
import useMediaQuery from "../common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../utils/screenSizes";
import { useTranslation } from "react-i18next";
import { CountrySelector } from "./components/CountrySelector";
import { useServerSideContent } from "~/common/hooks/useServerSideContent";
import RenderServerSideContent from "~/common/components/RenderServerSideContent";
import { LanguageSelector } from "~/feature/CategoryNavigation/v2/components/LanguageSelector";

export interface ForkPageNavigationProps {
  title: string;
  subtitle: string;

  regions: {
    title: string;
    countries: ForkPageCountry[];
  }[];
}

export interface ForkPageCountry {
  title: string;
  url: string;
  openInNewTab: boolean;
}

export const ForkPageNavigation = (props: ForkPageNavigationProps) => {
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const { element: forkBackgroundEl } = useServerSideContent(`#forkBackground`);
  if (!forkBackgroundEl) {
    return null;
  }

  return (
    <>
      {forkBackgroundEl && (
        <div className="scha__fork__country__selection__container">
          <RenderServerSideContent content={forkBackgroundEl} />
          <div className="scha__fork__country__selector">
            <h1 className="scha__fork__title">{props.title}</h1>
            <CountrySelector />
          </div>
        </div>
      )}
      {isDesktop ? <DesktopForkPageNavigation {...props} /> : <MobileForkPageNavigation {...props} />}
    </>
  );
};

const DesktopForkPageNavigation = ({ subtitle, regions }: ForkPageNavigationProps) => {
  const { t } = useTranslation();

  const maxCountries = Math.max(...regions.map((region) => region.countries.length));
  const countriesPerColumn = Math.floor((maxCountries + 1) / 2);

  const makeLink = ({ title, url, openInNewTab }: ForkPageCountry) => (
    <Link key={title} href={url} target={openInNewTab ? "_blank" : undefined}>
      {t(title)}
    </Link>
  );

  return (
    <>
      <div className="scha__fork__nav__container">
        <h2 className="scha__fork__subtitle">{subtitle}</h2>
        <div className="scha__fork__regions">
          {regions.map((region) => (
            <div className="scha__fork__region">
              <p className="scha__fork__region__name">{t(region.title)}</p>

              <div className="scha__fork__region__countries">
                <div className="scha__fork__region__countries__column">
                  {region.countries.slice(0, countriesPerColumn).map(makeLink)}
                </div>

                {region.countries.length > countriesPerColumn && (
                  <div className="scha__fork__region__countries__column">
                    {region.countries.slice(countriesPerColumn).map(makeLink)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <hr className="scha__fork__underline fullbleed" />
      <LanguageSelector />
    </>
  );
};

export const createForkPageNavigation = ({ rootId }: { rootId: string }) => {
  const root = document.getElementById(rootId)!;

  const title = root.querySelector(".scha__fork__title")?.textContent ?? "";
  const subtitle = root.querySelector(".scha__fork__subtitle")?.textContent ?? "";

  const regions = Array.prototype.slice.call(root.querySelectorAll(".scha__fork__region")).map((regionRoot) => {
    const countries = Array.prototype.slice.call(regionRoot.querySelectorAll("a")).map((node) => ({
      title: node?.textContent,
      url: node?.getAttribute("href") ?? "",
      openInNewTab: !!node?.getAttribute("target"),
    }));

    return {
      title: regionRoot.querySelector(".scha__fork__region__name")?.textContent ?? "",
      countries,
    };
  });

  const props: ForkPageNavigationProps = {
    title,
    subtitle,
    regions,
  };

  return <ForkPageNavigation {...props} />;
};
